"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import {
  SEQUENCE_TAB_ITEMS,
  matchSequenceRoute,
  type SequenceTabItem,
} from "@/features/sequences/components/detail/sequence-tab-items";
import { useShowInboxTab } from "@/features/inbox/hooks/use-show-inbox-tab";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/stores/sidebar-store";

import { PanelBackButton } from "./panel-back-button";

function SequencePanelRow({
  item,
  href,
  active,
  onActiveClick,
}: {
  item: SequenceTabItem;
  href: string;
  active: boolean;
  onActiveClick: () => void;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      onClick={active ? onActiveClick : undefined}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-md px-2 transition-colors",
        active
          ? "sidebar-active text-heading"
          : "text-contrast/65 hover:bg-contrast/4 hover:text-heading",
      )}
    >
      <Icon
        name={item.icon}
        size={16}
        className={cn(
          "size-4 shrink-0 text-contrast",
          active ? "opacity-90" : "opacity-[0.45]",
        )}
      />
      <span className="flex-1 truncate text-sm font-medium leading-5">
        {item.label}
      </span>
    </Link>
  );
}

export function SequencePanel({ onBack }: { onBack: () => void }) {
  const pathname = usePathname();
  const { setOpenMobile } = useSidebar();
  const match = matchSequenceRoute(pathname);

  // Spec 06 §4.6 — same Inbox visibility rule as the on-page strip
  // (`SequenceTabsNav`); called unconditionally to satisfy the rules of
  // hooks even though `match` may be null on an unmatched pathname.
  const showInboxTab = useShowInboxTab(match?.sequenceId ?? "");
  const inboxTabHidden = showInboxTab === false;

  // Only reachable via the "Sequence" drill row in `JobPanel` (or the
  // contextual initial-panel resolution), both gated on a matching sequence
  // route — so this should be unreachable in practice. Guard anyway rather
  // than crash on a null jobId/sequenceId.
  if (!match) return null;

  const base = `/jobs/${match.jobId}/sequences/${match.sequenceId}`;
  const items = inboxTabHidden
    ? SEQUENCE_TAB_ITEMS.filter((t) => t.value !== "inbox")
    : SEQUENCE_TAB_ITEMS;

  return (
    <nav
      aria-label="Sequence"
      className="flex flex-col gap-0.5 px-3.5 py-2.5"
    >
      <PanelBackButton onBack={onBack} />

      {items.map((item) => {
        const href = `${base}/${item.value}`;
        const active = pathname?.startsWith(href) ?? false;
        return (
          <SequencePanelRow
            key={item.value}
            item={item}
            href={href}
            active={active}
            onActiveClick={() => setOpenMobile(false)}
          />
        );
      })}
    </nav>
  );
}
